import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

// Wire format mirrors src/flowstack/server/routes/admin/ (fs#1212, U1.3).
// Every route there is the GLOBAL platform admin's alone and re-checks the
// role on each request; `useAdminAccess` only decides what the SPA renders.

export type OrgStatus = "active" | "suspended" | "archived";

export interface AdminOrg {
  org_id: string;
  name: string;
  status: OrgStatus;
  max_security_level: number;
  created_at: string;
  suspended_at: string | null;
  archived_at: string | null;
  offboarded_at: string | null;
}

export interface AdminMember {
  user_id: string;
  roles: string[];
  invited_by: string | null;
  joined_at: string;
}

export interface AdminEntitlement {
  subject_kind: "tile" | "flow";
  subject_name: string;
  grant_kind: string;
  granted_by: string;
  granted_at: string;
  revoked_at: string | null;
}

export interface AdminAuditEvent {
  event_id: number;
  actor_user_id: string;
  action: string;
  target: string;
  previous_value: unknown;
  new_value: unknown;
  reason: string;
  correlation_id: string | null;
  at: string;
}

export interface AuditPage {
  events: AdminAuditEvent[];
  /** Pass as `before` for the next, older page; `null` on the last one. */
  next_before: number | null;
}

/** The fixed role vocabulary (C1.1), in the order the admin pages list it. */
export const ROLES = [
  "scientific_user",
  "org_owner",
  "finance",
  "support_agent",
  "platform_admin",
] as const;

/** The grant kinds Phase 1 ships (C1.4). */
export const GRANT_KINDS = ["free", "admin_grant", "org_contract", "beta"] as const;

/** Where each status may move (mirrors `orgs.repo.STATUS_TRANSITIONS`):
 * archive is final. */
export const STATUS_TRANSITIONS: Record<OrgStatus, OrgStatus[]> = {
  active: ["suspended", "archived"],
  suspended: ["active", "archived"],
  archived: [],
};

/** The prefix every admin query key shares; a write invalidates it whole. */
export const ADMIN_ROOT_QUERY_KEY = ["admin"] as const;
export const ADMIN_ACCESS_QUERY_KEY = [...ADMIN_ROOT_QUERY_KEY, "access"] as const;
export const ADMIN_ORGS_QUERY_KEY = [...ADMIN_ROOT_QUERY_KEY, "orgs"] as const;

export function adminOrgQueryKey(orgId: string): readonly unknown[] {
  return [...ADMIN_ROOT_QUERY_KEY, "org", orgId] as const;
}

function orgPath(orgId: string, suffix = ""): string {
  return `/api/admin/orgs/${encodeURIComponent(orgId)}${suffix}`;
}

/** Whether the viewer is a global platform admin. `false` until the answer
 * arrives, and on any error, so the admin nav never flashes. */
export function useAdminAccess(): boolean {
  const { data } = useQuery({
    queryKey: ADMIN_ACCESS_QUERY_KEY,
    queryFn: () => apiFetch<{ platform_admin: boolean }>("/api/admin/access"),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data?.platform_admin === true;
}

export function useAdminOrgs() {
  return useQuery({
    queryKey: ADMIN_ORGS_QUERY_KEY,
    queryFn: async (): Promise<AdminOrg[]> =>
      (await apiFetch<{ orgs: AdminOrg[] }>("/api/admin/orgs")).orgs,
  });
}

export function useAdminOrg(orgId: string) {
  return useQuery({
    queryKey: adminOrgQueryKey(orgId),
    queryFn: () => apiFetch<AdminOrg>(orgPath(orgId)),
    enabled: Boolean(orgId),
  });
}

export function useAdminMembers(orgId: string) {
  return useQuery({
    queryKey: [...adminOrgQueryKey(orgId), "members"],
    queryFn: async (): Promise<AdminMember[]> =>
      (await apiFetch<{ members: AdminMember[] }>(orgPath(orgId, "/members"))).members,
    enabled: Boolean(orgId),
  });
}

export function useAdminEntitlements(orgId: string, includeRevoked: boolean) {
  return useQuery({
    queryKey: [...adminOrgQueryKey(orgId), "entitlements", includeRevoked],
    queryFn: async (): Promise<AdminEntitlement[]> =>
      (
        await apiFetch<{ entitlements: AdminEntitlement[] }>(
          orgPath(orgId, `/entitlements${includeRevoked ? "?include_revoked=true" : ""}`)
        )
      ).entitlements,
    enabled: Boolean(orgId),
  });
}

export function useAdminAudit(orgId: string, before: number | null) {
  return useQuery({
    queryKey: [...adminOrgQueryKey(orgId), "audit", before],
    queryFn: () =>
      apiFetch<AuditPage>(orgPath(orgId, `/audit${before === null ? "" : `?before=${before}`}`)),
    enabled: Boolean(orgId),
  });
}

/** Every admin write changes what the admin pages show: drop the whole
 * `admin` prefix on success. */
function useAdminWrite<TVars, TResult>(write: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_ROOT_QUERY_KEY });
    },
  });
}

export interface CreateOrgVars {
  org_id: string;
  name: string;
  reason: string;
}

export function useCreateOrg() {
  return useAdminWrite((json: CreateOrgVars) =>
    apiFetch<{ org: AdminOrg }>("/api/admin/orgs", { method: "POST", json })
  );
}

export function useRenameOrg(orgId: string) {
  return useAdminWrite((json: { name: string; reason: string }) =>
    apiFetch<{ org: AdminOrg }>(orgPath(orgId), { method: "PATCH", json })
  );
}

export function useSetOrgStatus(orgId: string) {
  return useAdminWrite((json: { status: OrgStatus; reason: string }) =>
    apiFetch<{ org: AdminOrg }>(orgPath(orgId, "/status"), { method: "POST", json })
  );
}

/** U1.2's audited route — the one place an org's ceiling changes. */
export function useSetSecurityLevel(orgId: string) {
  return useAdminWrite((json: { max_security_level: number; reason: string }) =>
    apiFetch(`/api/orgs/${encodeURIComponent(orgId)}/security-level`, { method: "PUT", json })
  );
}

export function useAddMember(orgId: string) {
  return useAdminWrite((json: { user_id: string; roles: string[]; reason: string }) =>
    apiFetch(orgPath(orgId, "/members"), { method: "POST", json })
  );
}

export function useSetMemberRoles(orgId: string) {
  return useAdminWrite(({ userId, ...json }: { userId: string; roles: string[]; reason: string }) =>
    apiFetch(orgPath(orgId, `/members/${encodeURIComponent(userId)}`), { method: "PUT", json })
  );
}

export function useRemoveMember(orgId: string) {
  return useAdminWrite(({ userId, reason }: { userId: string; reason: string }) =>
    apiFetch(orgPath(orgId, `/members/${encodeURIComponent(userId)}`), {
      method: "DELETE",
      json: { reason },
    })
  );
}

export interface GrantVars {
  subject_kind: "tile" | "flow";
  subject_name: string;
  grant_kind: string;
  reason: string;
}

export function useGrantEntitlement(orgId: string) {
  return useAdminWrite((json: GrantVars) =>
    apiFetch(orgPath(orgId, "/entitlements"), { method: "POST", json })
  );
}

/** The org's VCM binding (`routes/admin/compute.py`, fs#1217): secret NAME only. */
export interface ComputeBinding {
  org_id: string;
  provisioned: boolean;
  /** The pilot or a reserved id: bound by its seed migration, never provisioned here. */
  reserved: boolean;
  vcm_project_id: string | null;
  vcm_key_secret_name: string | null;
  /** `null`: the default account, named after the org. */
  vcm_billing_account_id: string | null;
}

export interface ProvisionComputeResult extends ComputeBinding {
  created: boolean;
  drift: string[];
  shared_funding_with: string[];
  correlation_id: string;
}

export interface ProvisionComputeVars {
  contact_email: string;
  /** Omitted: a new account named after the org. */
  billing_account_id?: string;
  shared_funding: boolean;
  reason: string;
}

export function useAdminCompute(orgId: string) {
  return useQuery({
    queryKey: [...adminOrgQueryKey(orgId), "compute"],
    queryFn: () => apiFetch<ComputeBinding>(orgPath(orgId, "/compute")),
    enabled: Boolean(orgId),
  });
}

/** "Provision compute": one call to the compute controller, then the binding
 * is stored once. Never retried here — U3.1 rolls nothing back, so the admin
 * re-sends the same request. */
export function useProvisionCompute(orgId: string) {
  return useAdminWrite((json: ProvisionComputeVars) =>
    apiFetch<ProvisionComputeResult>(orgPath(orgId, "/compute"), { method: "POST", json })
  );
}

export function useRevokeEntitlement(orgId: string) {
  return useAdminWrite(
    (json: { subject_kind: "tile" | "flow"; subject_name: string; reason: string }) =>
      apiFetch(orgPath(orgId, "/entitlements/revoke"), { method: "POST", json })
  );
}
